import React, { useEffect, useState, useRef } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  FaFacebookF,
  FaLinkedinIn,
  FaPhoneAlt,
  FaChevronDown,
  FaUser,
  FaSignOutAlt,
  FaTachometerAlt,
  FaUserGraduate,
  FaChalkboardTeacher,
  FaUserShield
} from "react-icons/fa";
import { FaInstagram, FaXTwitter ,FaYoutube} from "react-icons/fa6";
import { useAuth } from "../context/AuthContext";
import logo from "../assets/NIECCE Logos.avif";
import "./Header.css";

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const headerRef = useRef(null);
  const userMenuRef = useRef(null);
  const { user, logout } = useAuth();

  const toggleMenu = () => setMenuOpen((prev) => !prev);
  const closeMenus = () => {
    setMenuOpen(false);
    setSubmenuOpen(null);
    setUserMenuOpen(false);
  };

  const handleMouseEnter = (menu) => setSubmenuOpen(menu);
  const handleMouseLeave = () => setSubmenuOpen(null);

  const toggleUserMenu = () => setUserMenuOpen((prev) => !prev);

  const handleLogout = () => {
    logout();
    navigate("/login");
    closeMenus();
  };

  const getDashboardLink = () => {
    if (!user) return null;
    
    switch(user.role) {
      case 'admin':
        return '/admin';
      case 'teacher':
        return '/teacher';
      case 'student':
        return '/student';
      default:
        return '/';
    }
  };

  const getUserIcon = () => {
    if (!user) return <FaUser />;
    
    switch(user.role) {
      case 'admin':
        return <FaUserShield />;
      case 'teacher':
        return <FaChalkboardTeacher />;
      case 'student':
        return <FaUserGraduate />;
      default:
        return <FaUser />;
    }
  };

  const getUserRoleText = () => {
    if (!user) return '';
    
    switch(user.role) {
      case 'admin':
        return 'Admin';
      case 'teacher':
        return 'Teacher';
      case 'student':
        return 'Student';
      default:
        return 'User';
    }
  };

  const slugify = (text) =>
    text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  const isParentActive = (menu) => {
    if (menu === "destinations") {
      const destinations = ["about", "awards-and-recognitions", "competitions"];
      return destinations.some(
        (item) => decodeURIComponent(location.pathname) === `/${item}`
      );
    } else if (menu === "services") {
      const services = ["ntt-mttcourse", "ptt-course", "results", "admission"];
      return services.some(
        (item) => decodeURIComponent(location.pathname) === `/courses/${item}`
      );
    }
    return false;
  };

  useEffect(() => {
    closeMenus();
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setSubmenuOpen(null);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header ref={headerRef} className="animated-header">
      {/* Top Bar */}
      <div className="top-bar">
        <div className="container d-flex flex-wrap justify-content-between align-items-center py-1">
          <div className="d-flex gap-3 social-icons">
            <a href="https://www.instagram.com/niecceinstitute/" target="_blank" aria-label="Instagram">
              <FaInstagram />
            </a>
            <a href="https://www.facebook.com/NIECCEofficial" target="_blank" aria-label="Facebook">
              <FaFacebookF />
            </a>
            <a href="https://www.youtube.com/@nieccentt6307" target="_blank" aria-label="YouTube">
              <FaYoutube />
            </a>
          </div>

          <div className="d-flex flex-wrap align-items-center gap-3 mt-1 mt-md-0 top-links">
            <span className="d-flex align-items-center">
              <FaPhoneAlt className="me-1 phone-icon" />
              Request Call Back:
              <strong className="ms-1"><a href="tel:+919886855700" className="text-reset text-decoration-none">+91 98868 55700</a></strong>
            </span>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav className="navbar navbar-expand-lg main-navbar py-3">
        <div className="container">
          <Link className="navbar-brand" to="/" onClick={closeMenus}>
            <img
              src={logo}
              alt="NIECCE teacher training institute logo"
              title="NIECCE teacher training institute"
              className="logo"
            />
          </Link>

          <button
            className="navbar-toggler"
            type="button"
            onClick={toggleMenu}
            aria-controls="navbarNav"
            aria-expanded={menuOpen}
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          <div className={`collapse navbar-collapse ${menuOpen ? "show" : ""}`} id="navbarNav">
            <ul className="navbar-nav mx-auto mb-2 mb-lg-0">

              {/* HOME MENU */}
              <li
                className="nav-item dropdown"
                onMouseEnter={() => handleMouseEnter("destinations")}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  type="button"
                  className={`nav-link dropdown-toggle ${
                    isParentActive("destinations") ? "active" : ""
                  }`}
                  onClick={() => navigate("/")}
                >
                  Home <FaChevronDown className="ms-1" />
                </button>
                <ul
                  className={`submenu dropdown-menu animated-dropdown ${
                    submenuOpen === "destinations" ? "show" : ""
                  }`}
                >
                  {["About", "Awards", "Competitions"].map((item) => (
                    <li key={item}>
                      <NavLink
                        to={`/${slugify(item)}`}
                        className="dropdown-item"
                        onClick={closeMenus}
                      >
                        {item}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </li>

              {/* COURSES MENU */}
              <li
                className="nav-item dropdown"
                onMouseEnter={() => handleMouseEnter("services")}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  type="button"
                  className={`nav-link dropdown-toggle ${
                    isParentActive("services") ? "active" : ""
                  }`}
                  onClick={() => navigate("/courses")}
                >
                  Courses <FaChevronDown className="ms-1" />
                </button>
                <ul
                  className={`submenu dropdown-menu animated-dropdown ${
                    submenuOpen === "services" ? "show" : ""
                  }`}
                >
                  {["NTT/MTTCourse", "PTT Course", "Results", "Admission"].map((service) => (
                    <li key={service}>
                      <NavLink
                        to={`/course/${slugify(service)}`}
                        className="dropdown-item"
                        onClick={closeMenus}
                      >
                        {service}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </li>

              {/* STATIC LINKS */}
              <li className="nav-item">
                <NavLink className="nav-link" to="/alumni" onClick={closeMenus}>
                  Alumni
                </NavLink>
              </li>

              <li className="nav-item">
                <NavLink className="nav-link" to="/partner" onClick={closeMenus}>
                  Partner
                </NavLink>
              </li>

              <li className="nav-item">
                <NavLink className="nav-link" to="/blogs" onClick={closeMenus}>
                  Blogs
                </NavLink>
              </li>

              <li className="nav-item">
                <NavLink className="nav-link" to="/events" onClick={closeMenus}>
                  Events
                </NavLink>
              </li>
            </ul>

            <div className="d-flex align-items-center gap-3">
              <Link to="/contact" className="cta-btn" onClick={closeMenus}>
                Contact Us
              </Link>

              {/* User Authentication Section */}
              {user ? (
                <div className="user-menu-container" ref={userMenuRef}>
                  <button 
                    className="user-menu-btn"
                    onClick={toggleUserMenu}
                  >
                    <span className="user-avatar">
                      {getUserIcon()}
                    </span>
                    <span className="user-name d-none d-md-inline">
                      {user.fullName?.split(' ')[0] || 'User'}
                    </span>
                    <FaChevronDown className="ms-1" />
                  </button>
                  
                  {userMenuOpen && (
                    <div className="user-dropdown">
                      <div className="user-info">
                        <div className="user-avatar-large">
                          {getUserIcon()}
                        </div>
                        <div className="user-details">
                          <h6>{user.fullName}</h6>
                          <span className="user-role">{getUserRoleText()}</span>
                          {user.enrollmentId && (
                            <span className="enrollment-id">ID: {user.enrollmentId}</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="dropdown-divider"></div>
                      
                      <Link 
                        to={getDashboardLink()} 
                        className="dropdown-item"
                        onClick={closeMenus}
                      >
                        <FaTachometerAlt /> Dashboard
                      </Link>
                      
                     
                      
                      <div className="dropdown-divider"></div>
                      
                      <button 
                        className="dropdown-item logout-btn"
                        onClick={handleLogout}
                      >
                        <FaSignOutAlt /> Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <Link to="/login" className="cta-btn cta-login" onClick={closeMenus}>
                    Login / Signup
                  </Link>
                  {/* <Link to="/apply" className="cta-btn cta-apply" onClick={closeMenus}>
                    Apply Now
                  </Link> */}
                </>
              )}
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
